import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CardActions, Button, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip, Snackbar, Alert, ImageList, ImageListItem,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Visibility as ViewIcon, VisibilityOff as HideIcon,
  PhotoLibrary as GalleryIcon, AddPhotoAlternate as AddPhotoIcon,
} from '@mui/icons-material';
import { galleryAPI } from '../services/api';

const errorText = (e, fallback) => e?.response?.data?.message || fallback;
const emptyForm = () => ({ title: '', description: '', eventDate: '' });

export default function GalleryManagement() {
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [files, setFiles] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [detail, setDetail] = useState(null); // full post with photos, for viewing/adding photos
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const fileInput = useRef(null);
  const addPhotoInput = useRef(null);

  const notify = (message, severity = 'success') => setSnack({ open: true, message, severity });

  const load = useCallback(async () => {
    try { setPosts((await galleryAPI.getAll()).data); }
    catch (e) { notify(errorText(e, 'Could not load the gallery'), 'error'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startCreate = () => { setForm(emptyForm()); setFiles([]); setOpen(true); };

  const save = async () => {
    if (form.title.trim().length < 3) { notify('Please enter a title', 'error'); return; }
    try {
      const fd = new FormData();
      fd.append('title', form.title.trim());
      if (form.description.trim()) fd.append('description', form.description.trim());
      if (form.eventDate) fd.append('eventDate', form.eventDate);
      files.forEach((f) => fd.append('photos', f));
      await galleryAPI.create(fd);
      setOpen(false);
      notify('Posted to the gallery');
      load();
    } catch (e) { notify(errorText(e, 'Could not publish this post'), 'error'); }
  };

  const togglePublish = async (post) => {
    try { await galleryAPI.setPublished(post.id, !post.published); load(); }
    catch (e) { notify(errorText(e, 'Could not update'), 'error'); }
  };

  const remove = async () => {
    try { await galleryAPI.delete(confirm.id); notify('Post deleted'); setConfirm(null); load(); }
    catch (e) { notify(errorText(e, 'Could not delete'), 'error'); }
  };

  const openDetail = async (post) => {
    try { setDetail((await galleryAPI.getOne(post.id)).data); }
    catch (e) { notify(errorText(e, 'Could not load the post'), 'error'); }
  };

  const addPhotosToDetail = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length || !detail) return;
    try {
      const fd = new FormData();
      picked.forEach((f) => fd.append('photos', f));
      await galleryAPI.addPhotos(detail.id, fd);
      notify('Photos added');
      openDetail(detail);
      load();
    } catch (err) { notify(errorText(err, 'Could not add photos'), 'error'); }
    if (addPhotoInput.current) addPhotoInput.current.value = '';
  };

  const removePhoto = async (photoId) => {
    try { await galleryAPI.deletePhoto(photoId); notify('Photo removed'); openDetail(detail); load(); }
    catch (e) { notify(errorText(e, 'Could not remove photo'), 'error'); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#004d40' }}>College Gallery</Typography>
          <Typography variant="body2" color="text.secondary">Share photos from college functions and events with students and faculty</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={startCreate}>New post</Button>
      </Box>

      {posts.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
          <GalleryIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
          <Typography>No posts yet. Click "New post" to share photos from a function or event.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {posts.map((p) => (
            <Grid item xs={12} sm={6} md={4} key={p.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700}>{p.title}</Typography>
                    <Chip size="small" label={p.published ? 'Published' : 'Hidden'} color={p.published ? 'success' : 'default'} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {p.eventDate || ''}{p.postedByName ? ` · ${p.postedByName}` : ''} · {p.photoCount} photo{p.photoCount === 1 ? '' : 's'}
                  </Typography>
                  {p.description && <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.description}</Typography>}
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Button size="small" onClick={() => openDetail(p)}>View photos</Button>
                  <Box>
                    <Tooltip title={p.published ? 'Hide from apps' : 'Publish'}>
                      <IconButton size="small" onClick={() => togglePublish(p)}>{p.published ? <HideIcon /> : <ViewIcon />}</IconButton>
                    </Tooltip>
                    <IconButton size="small" color="error" onClick={() => setConfirm(p)}><DeleteIcon /></IconButton>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#004d40', color: 'white' }}>Share function / event photos</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}><TextField fullWidth size="small" label="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="date" label="Event date" InputLabelProps={{ shrink: true }} value={form.eventDate} onChange={(e) => setForm({ ...form, eventDate: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" multiline rows={2} label="About" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" startIcon={<AddPhotoIcon />}>
                {files.length ? `${files.length} photo(s) selected` : 'Choose photos'}
                <input ref={fileInput} type="file" hidden multiple accept="image/*" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
              </Button>
            </Grid>
            {files.length > 0 && (
              <Grid item xs={12}>
                <ImageList cols={4} rowHeight={90} sx={{ m: 0 }}>
                  {files.map((f, i) => (
                    <ImageListItem key={i}><img src={URL.createObjectURL(f)} alt={f.name} style={{ objectFit: 'cover', height: 90 }} /></ImageListItem>
                  ))}
                </ImageList>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}>Publish</Button>
        </DialogActions>
      </Dialog>

      {/* Detail / manage photos dialog */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#004d40', color: 'white' }}>{detail?.title}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Button variant="outlined" component="label" startIcon={<AddPhotoIcon />} sx={{ mb: 2 }}>
            Add more photos
            <input ref={addPhotoInput} type="file" hidden multiple accept="image/*" onChange={addPhotosToDetail} />
          </Button>
          <ImageList cols={3} rowHeight={140}>
            {(detail?.photos || []).map((ph) => (
              <ImageListItem key={ph.id} sx={{ position: 'relative' }}>
                <img src={`/api${ph.url}`} alt={ph.caption || ''} style={{ objectFit: 'cover', height: 140 }} />
                <IconButton size="small" onClick={() => removePhoto(ph.id)}
                  sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,0.85)' }}>
                  <DeleteIcon fontSize="small" color="error" />
                </IconButton>
              </ImageListItem>
            ))}
          </ImageList>
          {(!detail?.photos || detail.photos.length === 0) && <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No photos in this post yet.</Typography>}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}><Button onClick={() => setDetail(null)}>Close</Button></DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete post?</DialogTitle>
        <DialogContent><Typography>"{confirm?.title}" and its photos will be removed. This cannot be undone.</Typography></DialogContent>
        <DialogActions sx={{ p: 2.5 }}><Button onClick={() => setConfirm(null)}>Cancel</Button><Button color="error" variant="contained" onClick={remove}>Delete</Button></DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
